// frontend/src/workshop/components/shapes/memory-frame/memory-frame.tsx
/**
 * Workshop MemoryFrame — Knowledge graph visualization using react-force-graph-2d.
 * Theme-aware (dark + light). No API calls. Mock data only.
 * Reference implementation: Denker_Chenin/components/memory/knowledge-graph.tsx
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import type { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';
import { forceCollide, forceRadial } from 'd3-force';

import { Icons } from '@/components/ui/icons';
import { FrameHeader } from '@/workshop/components/shapes/shared/frame-header';
import { FrameFooter } from '@/workshop/components/shapes/shared/frame-footer';
import { cn } from '@/lib/cn';

import { GRAPH_NODE_COLORS } from './types';
import type { ForceGraphData, ForceGraphNode, ForceGraphLink, GraphNodeType } from './types';
import { GRAPH_SCENARIOS } from './mock-data';
import type { GraphScenario } from './mock-data';

// ─── Constants ────────────────────────────────────────────────────────────────

const NODE_RADIUS = 4;
const SELECTED_NODE_RADIUS = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

interface LabelBox {
  x1: number; y1: number; x2: number; y2: number;
}

// D3 resolves source/target string IDs to node objects at runtime.
type ResolvedNode = NodeObject<ForceGraphNode>;
type ResolvedLink = LinkObject<ForceGraphNode, ForceGraphLink> & {
  source: ResolvedNode;
  target: ResolvedNode;
};

interface MemoryFrameProps {
  data?: ForceGraphData;
  scenario?: GraphScenario;
  title?: string;
  agentName?: string;
  className?: string;
  'data-testid'?: string;
  onResizeStart?: React.MouseEventHandler;
  onNodeClick?: (node: ForceGraphNode) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MemoryFrame({
  data,
  scenario = 'medium',
  title = 'Knowledge Graph',
  agentName,
  className,
  'data-testid': dataTestId,
  onResizeStart,
  onNodeClick,
}: MemoryFrameProps) {
  const graphRef = useRef<ForceGraphMethods<ForceGraphNode, ForceGraphLink>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelBoxesRef = useRef<LabelBox[]>([]);

  const [dimensions, setDimensions] = useState({ width: 400, height: 300 });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains('dark'));
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Theme-derived canvas colors — memoized so useCallback deps stay stable
  // Uses CSS custom properties where exact token matches exist; raw values otherwise.
  const themeColors = useMemo(() => {
    const styles = getComputedStyle(document.documentElement);
    const canvas = styles.getPropertyValue('--color-canvas').trim() || (isDark ? '#0a0a0a' : '#fafafa');
    const glassStroke = styles.getPropertyValue('--color-glass-stroke-light').trim();
    const agentPurple = styles.getPropertyValue('--color-agent-researcher').trim() || (isDark ? '#A78BFA' : '#7C3AED');

    return {
      bgColor:        canvas,
      linkColor:      glassStroke ? glassStroke : (isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)'),
      linkHighlight:  isDark ? 'rgba(148, 163, 184, 0.7)'  : 'rgba(71, 85, 105, 0.6)',
      labelBgColor:   isDark ? 'rgba(15, 23, 42, 0.8)'     : 'rgba(255, 255, 255, 0.9)',
      labelTextColor: isDark ? 'rgba(148, 163, 184, 0.85)' : 'rgba(71, 85, 105, 0.85)',
      labelActiveText: isDark ? 'rgba(226, 232, 240, 0.95)' : 'rgba(15, 23, 42, 0.95)',
      catGlowColor:   isDark ? 'rgba(167, 139, 250, 0.4)'  : 'rgba(139, 92, 246, 0.15)',
      catDotColor:    agentPurple,
      catLabelBg:     isDark ? 'rgba(30, 20, 60, 0.85)'    : 'rgba(237, 233, 254, 0.9)',
      catLabelText:   isDark ? '#C4B5FD'                   : '#5B21B6',
    };
  }, [isDark]);

  const graphData = data ?? GRAPH_SCENARIOS[scenario];

  // ─── Responsive dimensions ──────────────────────────────────────────────────

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: Math.floor(entry.contentRect.width),
          height: Math.floor(entry.contentRect.height),
        });
      }
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ─── Force simulation config ─────────────────────────────────────────────────

  useEffect(() => {
    const typeMap = new Map<string, GraphNodeType>();
    const countMap = new Map<string, number>();
    for (const node of graphData.nodes) {
      typeMap.set(node.id, node.type);
      countMap.set(node.id, 0);
    }
    for (const link of graphData.links) {
      const src = typeof link.source === 'string' ? link.source : (link.source as ResolvedNode).id as string;
      const tgt = typeof link.target === 'string' ? link.target : (link.target as ResolvedNode).id as string;
      countMap.set(src, (countMap.get(src) ?? 0) + 1);
      countMap.set(tgt, (countMap.get(tgt) ?? 0) + 1);
    }

    const fg = graphRef.current;
    if (!fg) return;

    fg.d3Force('link')?.distance?.((link: ResolvedLink) => {
      const srcId = link.source.id as string;
      const tgtId = link.target.id as string;
      const srcType = typeMap.get(srcId);
      const tgtType = typeMap.get(tgtId);
      const avgLinks = ((countMap.get(srcId) ?? 0) + (countMap.get(tgtId) ?? 0)) / 2;

      if (srcType === 'user_profile' && tgtType === 'category') return 250 + avgLinks * 10;
      if (srcType === 'category' || tgtType === 'category') return 180 + avgLinks * 10;
      const maxLinks = Math.max(countMap.get(srcId) ?? 0, countMap.get(tgtId) ?? 0);
      if (maxLinks >= 5) return 200 + avgLinks * 10;
      return 120 + avgLinks * 15;
    });

    fg.d3Force('charge')?.strength?.((node: ResolvedNode) => {
      const links = countMap.get(node.id as string) ?? 0;
      const type = typeMap.get(node.id as string);
      if (type === 'user_profile') return -800 - links * 30;
      if (type === 'category')     return -500 - links * 40;
      return links > 0 ? -300 - links * 30 : -80;
    });

    fg.d3Force('collision', forceCollide<ResolvedNode>().radius((node) => {
      const type = typeMap.get(node.id as string);
      if (type === 'category')     return 120;
      if (type === 'user_profile') return 100;
      return 80;
    }).strength(0.8));

    const totalNodes = graphData.nodes.length;
    const connectedCount = Array.from(countMap.values()).filter((c) => c > 0).length;
    const hasIsolated = connectedCount < totalNodes;

    fg.d3Force('center')?.strength?.(hasIsolated ? 0.1 : 0.03);

    if (hasIsolated) {
      fg.d3Force('radial', forceRadial<ResolvedNode>(200, 0, 0).strength((node) => {
        return (countMap.get(node.id as string) ?? 0) === 0 ? 0.3 : 0;
      }));
    } else {
      fg.d3Force('radial', null);
    }

    fg.d3ReheatSimulation();
  }, [graphData]);

  // ─── Canvas: node rendering ──────────────────────────────────────────────────

  const nodeCanvasObject = useCallback((
    nodeRaw: NodeObject<ForceGraphNode>,
    ctx: CanvasRenderingContext2D,
    globalScale: number,
  ) => {
    const node = nodeRaw as NodeObject<ForceGraphNode> & ForceGraphNode;
    if (node.x === undefined || node.y === undefined) return;

    const isSelected = selectedNodeId === (node.id as string);
    const isHovered  = hoveredNode === (node.id as string);
    const nodeColor  = node.color || GRAPH_NODE_COLORS[node.type] || '#94A3B8';
    const radius     = isSelected ? SELECTED_NODE_RADIUS : isHovered ? NODE_RADIUS + 0.5 : NODE_RADIUS;
    const label      = node.label || node.name;

    // ── Category node: violet with always-visible label ──
    if (node.type === 'category') {
      const cr = radius + 2;
      const glow = ctx.createRadialGradient(node.x, node.y, cr, node.x, node.y, cr + 4);
      glow.addColorStop(0, themeColors.catGlowColor);
      glow.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(node.x, node.y, cr + 4, 0, 2 * Math.PI);
      ctx.fillStyle = glow; ctx.fill();

      ctx.beginPath(); ctx.arc(node.x, node.y, cr, 0, 2 * Math.PI);
      ctx.fillStyle = themeColors.catDotColor; ctx.fill();

      const fs = Math.max(9, 10 / globalScale);
      ctx.font = `600 ${fs}px Inter, system-ui, sans-serif`;
      const tw = ctx.measureText(label).width;
      const pad = 4;
      const ly = node.y + cr + 8;
      ctx.fillStyle = themeColors.catLabelBg;
      ctx.beginPath();
      ctx.roundRect(node.x - tw / 2 - pad, ly - fs / 2 - pad / 2, tw + pad * 2, fs + pad, 4);
      ctx.fill();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = themeColors.catLabelText;
      ctx.fillText(label, node.x, ly);
      // Push category label box so adjacent labels suppress themselves
      labelBoxesRef.current.push({
        x1: node.x - tw / 2 - pad,
        y1: ly - fs / 2 - pad / 2,
        x2: node.x + tw / 2 + pad,
        y2: ly + fs / 2 + pad / 2,
      });
      return;
    }

    // ── user_profile node: golden gradient ──
    if (node.type === 'user_profile') {
      const glow = ctx.createRadialGradient(node.x, node.y, radius, node.x, node.y, radius + 3);
      glow.addColorStop(0, 'rgba(255, 215, 0, 0.5)');
      glow.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI);
      ctx.fillStyle = glow; ctx.fill();

      ctx.beginPath(); ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      const grad = ctx.createLinearGradient(node.x - radius, node.y - radius, node.x + radius, node.y + radius);
      grad.addColorStop(0, '#FFD700');
      grad.addColorStop(0.3, '#FFC107');
      grad.addColorStop(0.6, '#FFDF00');
      grad.addColorStop(1, '#DAA520');
      ctx.fillStyle = grad; ctx.fill();
    } else {
      // ── Standard node: glow ring + solid dot ──
      const glowR = isSelected || isHovered ? radius + 4 : radius + 3;
      const outerGlow = ctx.createRadialGradient(node.x, node.y, radius, node.x, node.y, glowR);
      outerGlow.addColorStop(0, `${nodeColor}${isSelected || isHovered ? '60' : '20'}`);
      outerGlow.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(node.x, node.y, glowR, 0, 2 * Math.PI);
      ctx.fillStyle = outerGlow; ctx.fill();

      if (isSelected || isHovered) {
        const innerGlow = ctx.createRadialGradient(node.x, node.y, radius, node.x, node.y, radius + 2);
        innerGlow.addColorStop(0, `${nodeColor}40`);
        innerGlow.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(node.x, node.y, radius + 2, 0, 2 * Math.PI);
        ctx.fillStyle = innerGlow; ctx.fill();
      }

      ctx.beginPath(); ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = nodeColor; ctx.fill();
    }

    // ── Label: show when zoomed in or selected/hovered ──
    if (globalScale > 0.8 || isSelected || isHovered) {
      const fs = Math.max(9, 10 / globalScale);
      ctx.font = `500 ${fs}px Inter, system-ui, sans-serif`;
      const tw = ctx.measureText(label).width;
      const pad = 3;
      const ly = node.y + radius + 8;

      const box: LabelBox = {
        x1: node.x - tw / 2 - pad,
        y1: ly - fs / 2 - pad / 2,
        x2: node.x + tw / 2 + pad,
        y2: ly + fs / 2 + pad / 2,
      };

      const isHighPriority = isSelected || isHovered;
      const hasOverlap = !isHighPriority && labelBoxesRef.current.some(
        (b) => box.x1 < b.x2 && box.x2 > b.x1 && box.y1 < b.y2 && box.y2 > b.y1,
      );

      if (!hasOverlap) {
        labelBoxesRef.current.push(box);
        ctx.fillStyle = themeColors.labelBgColor;
        ctx.beginPath();
        ctx.roundRect(box.x1, box.y1, tw + pad * 2, fs + pad, 3);
        ctx.fill();
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = isSelected || isHovered ? themeColors.labelActiveText : themeColors.labelTextColor;
        ctx.fillText(label, node.x, ly);
      }
    }
  }, [selectedNodeId, hoveredNode, themeColors]);

  // ─── Canvas: link rendering ──────────────────────────────────────────────────

  const linkCanvasObject = useCallback((
    linkRaw: LinkObject<ForceGraphNode, ForceGraphLink>,
    ctx: CanvasRenderingContext2D,
    globalScale: number,
  ) => {
    const link = linkRaw as ResolvedLink;
    if (
      link.source?.x == null ||
      link.source?.y == null ||
      link.target?.x == null ||
      link.target?.y == null
    ) return;

    const srcId = link.source.id as string | undefined;
    const tgtId = link.target.id as string | undefined;
    const isHighlighted =
      selectedNodeId === srcId ||
      selectedNodeId === tgtId ||
      hoveredNode    === srcId ||
      hoveredNode    === tgtId;

    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.strokeStyle = isHighlighted ? themeColors.linkHighlight : themeColors.linkColor;
    ctx.lineWidth = (isHighlighted ? 1.5 : 1) / globalScale;
    ctx.setLineDash(isHighlighted ? [] : [3, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    if (link.label && globalScale > 1.5 && isHighlighted) {
      const midX = (link.source.x + link.target.x) / 2;
      const midY = (link.source.y + link.target.y) / 2;
      const fs = 8 / globalScale;
      ctx.font = `${fs}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(148, 163, 184, 0.6)';
      ctx.fillText(link.label, midX, midY);
    }
  }, [selectedNodeId, hoveredNode, themeColors]);

  // ─── Interaction handlers ────────────────────────────────────────────────────

  const handleNodeClick = useCallback((nodeRaw: NodeObject<ForceGraphNode>) => {
    const id = nodeRaw.id as string;
    setSelectedNodeId((prev) => (prev === id ? null : id));
    onNodeClick?.(nodeRaw as NodeObject<ForceGraphNode> & ForceGraphNode);
  }, [onNodeClick]);

  const handleNodeHover = useCallback((nodeRaw: NodeObject<ForceGraphNode> | null) => {
    setHoveredNode(nodeRaw ? (nodeRaw.id as string) : null);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // ─── Zoom controls ───────────────────────────────────────────────────────────

  const handleZoomIn    = useCallback(() => { const fg = graphRef.current; if (fg) fg.zoom(fg.zoom() * 1.3, 300); }, []);
  const handleZoomOut   = useCallback(() => { const fg = graphRef.current; if (fg) fg.zoom(fg.zoom() * 0.75, 300); }, []);
  const handleZoomReset = useCallback(() => graphRef.current?.zoomToFit(400, 50), []);

  const handleRenderFramePre = useCallback(() => {
    labelBoxesRef.current = [];
  }, []);

  const handleNodePointerAreaPaint = useCallback((nodeRaw: NodeObject<ForceGraphNode>, color: string, ctx: CanvasRenderingContext2D) => {
    if (nodeRaw.x === undefined || nodeRaw.y === undefined) return;
    ctx.beginPath();
    ctx.arc(nodeRaw.x, nodeRaw.y, NODE_RADIUS + 12, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        'denker-glass-frame flex flex-col rounded-frame overflow-hidden',
        className,
      )}
      data-testid={dataTestId ?? 'memory-frame'}
    >
      <FrameHeader
        title={title}
        icon={Icons.Brain}
        accentColor="bg-frame-memory"
        agentName={agentName}
        data-testid="memory-frame-header"
      />

      <div className="flex flex-1 flex-col gap-1 p-2 overflow-hidden">
        <div
          ref={containerRef}
          className="relative flex-1 rounded border border-border bg-canvas overflow-hidden"
          style={{ minHeight: 160 }}
          data-testid="memory-frame-graph"
        >
          {dimensions.width > 0 && (
            <ForceGraph2D
              ref={graphRef as React.RefObject<ForceGraphMethods<ForceGraphNode, ForceGraphLink>>}
              graphData={graphData}
              width={dimensions.width}
              height={dimensions.height}
              backgroundColor={themeColors.bgColor}
              onRenderFramePre={handleRenderFramePre}
              nodeCanvasObject={nodeCanvasObject}
              nodePointerAreaPaint={handleNodePointerAreaPaint}
              linkCanvasObject={linkCanvasObject}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              onBackgroundClick={handleBackgroundClick}
              warmupTicks={300}
              cooldownTicks={200}
              d3AlphaDecay={0.02}
              d3VelocityDecay={0.3}
              enableNodeDrag={true}
              enableZoomInteraction={true}
              enablePanInteraction={true}
              minZoom={0.3}
              maxZoom={4}
            />
          )}

          <div
            className="absolute bottom-2 right-2 flex flex-col gap-1"
            data-testid="memory-frame-zoom-controls"
          >
            {([
              { icon: Icons.Plus,       handler: handleZoomIn,    testId: 'memory-frame-zoom-in'    },
              { icon: Icons.Minus,      handler: handleZoomOut,   testId: 'memory-frame-zoom-out'   },
              { icon: Icons.RotateCcw,  handler: handleZoomReset, testId: 'memory-frame-zoom-reset' },
            ] as const).map(({ icon: Icon, handler, testId }) => (
              <button
                key={testId}
                onClick={handler}
                data-testid={testId}
                className="flex h-6 w-6 items-center justify-center rounded bg-surface/80 border border-border text-muted hover:text-foreground hover:bg-surface transition-colors"
              >
                <Icon className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <FrameFooter onResizeStart={onResizeStart} data-testid="memory-frame-footer" />
    </div>
  );
}

export type { MemoryFrameProps };
